import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { usePricing } from "./useHook";
import PricingModal from "../../Components/Modals/Pricing/PricingModal";

function Pricing() {
  const { getPricing, addPricing, updatePricing, deletePricing } = usePricing();

  const [pricingList, setPricingList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const openModalForAdd = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (pricingItem) => {
    setEditData(pricingItem);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const fetchPricing = async () => {
    await getPricing(setPricingList);
  };

  useEffect(() => {
    fetchPricing();
    // eslint-disable-next-line
  }, []);

  const handleSubmitPricing = async (formData) => {
    try {
      if (formData.id) {
        // Update existing record if editing
        const response = await updatePricing(formData.id, formData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Pricing Updated",
            text: "The pricing record has been updated successfully!",
          });
        }
      } else {
        // Add new record
        const response = await addPricing(formData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Pricing Added",
            text: "The new pricing record has been added successfully!",
          });
        }
      }
      fetchPricing();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          (formData.id
            ? "Failed to update pricing."
            : "Failed to add pricing.") + " Please try again.",
      });
      console.error("Failed to submit pricing:", error);
    } finally {
      closeModal();
    }
  };

  const handleDelete = async (id) => {
    const confirmResult = await Swal.fire({
      icon: "warning",
      title: "Delete Pricing",
      text: "Are you sure you want to delete this pricing record?",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (confirmResult.isConfirmed) {
      try {
        await deletePricing(id);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The pricing record has been deleted.",
        });
        fetchPricing();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete pricing record. Please try again.",
        });
      }
    }
  };

  return (
    <>
      {/* Header */}
      <div className="bg-gray-100 p-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Manage Pricing</h2>
        <button
          className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded shadow"
          onClick={openModalForAdd}
        >
          Add Pricing
        </button>
      </div>

      {/* Pricing Table */}
      <div className="mt-4">
        <div className="overflow-x-auto mt-6">
          <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
                <th className="p-4 text-left">Sr. No</th>
                <th className="p-4 text-left">Plot Size</th>
                <th className="p-4 text-left">Layout</th>
                <th className="p-4 text-left">Structure</th>
                <th className="p-4 text-left">Interior</th>
                <th className="p-4 text-left">BoQs</th>
                <th className="p-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pricingList?.map((item, index) => (
                <tr
                  key={item.id}
                  className="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td className="p-4">{index + 1}</td>
                  <td className="p-4">{item.plotSize}</td>
                  <td className="p-4">{item.layout}</td>
                  <td className="p-4">{item.structure}</td>
                  <td className="p-4">{item.interiorDesign}</td>
                  <td className="p-4">{item.boQs}</td>
                  <td className="p-4 flex space-x-2">
                    <button
                      className="bg-yellow-500 hover:bg-yellow-600 text-white py-1 px-3 rounded shadow"
                      onClick={() => openModalForEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-red-500 hover:bg-red-600 text-white py-1 px-3 rounded shadow"
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {pricingList.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-gray-500">
                    No pricing records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Add/Edit Pricing */}
      <PricingModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmitPricing}
        editData={editData}
      />
    </>
  );
}

export default Pricing;
